import React, { useRef, useState } from "react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Chip } from "@/components/Shared";
import { UploadSimple, DownloadSimple, FileXls } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_VARIANT = { valid: "success", duplicate: "neutral", error: "danger" };

export default function FlatsMasterImport({ open, onOpenChange, onDone }) {
    const fileRef = useRef();
    const [file, setFile] = useState(null);
    const [busy, setBusy] = useState(false);
    const [phase, setPhase] = useState("select"); // select | preview | result
    const [report, setReport] = useState(null);
    const [commitResult, setCommitResult] = useState(null);

    function reset() {
        setFile(null); setPhase("select"); setReport(null); setCommitResult(null);
    }
    function close() {
        reset();
        onOpenChange(false);
    }

    async function downloadTemplate() {
        try {
            const res = await api.get("/flats/import-template", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "Maintayn-Flats-Master-Template.xlsx");
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function preview() {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/flats/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setReport(data);
            setPhase("preview");
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    async function commit() {
        if (!file) return;
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/flats/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setCommitResult(data);
            setPhase("result");
            toast.success(`${data.created_flats} flat(s) created · ${data.created_parking} parking slot(s) linked`);
            onDone?.();
        } catch (e) { toast.error(formatError(e)); }
        finally { setBusy(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => (!v ? close() : onOpenChange(v))}>
            <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl tracking-tight">Master Onboarding — Import Flats</DialogTitle>
                </DialogHeader>

                {phase === "select" && (
                    <div className="space-y-4">
                        <div className="bg-brand-sage/50 border border-brand-line rounded-sm p-4 text-sm">
                            <p className="font-medium text-brand-ink">Excel master template (3 sheets: Flats, Metadata, Instructions)</p>
                            <p className="text-brand-inkSoft mt-1">Download it, fill each flat's row, then upload it here for a validated preview before anything is created.</p>
                            <button data-testid="download-template-btn" onClick={downloadTemplate}
                                className="mt-3 inline-flex items-center gap-1 text-brand-action text-xs hover:underline">
                                <DownloadSimple size={14} /> Download template
                            </button>
                        </div>
                        <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                            data-testid="master-import-file-input"
                            onChange={(e) => setFile(e.target.files?.[0] || null)} />
                        <Button data-testid="master-import-pick-file" type="button" variant="outline" onClick={() => fileRef.current?.click()}
                            className="rounded-full border-brand-line w-full">
                            <FileXls size={14} className="mr-1.5" />
                            {file ? file.name : "Choose the filled .xlsx file"}
                        </Button>
                    </div>
                )}

                {phase === "preview" && report && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                            <MiniStat label="Total rows" value={report.total_rows} />
                            <MiniStat label="Valid" value={report.valid_rows} accent="success" />
                            <MiniStat label="Duplicate" value={report.duplicate_rows} accent="neutral" />
                            <MiniStat label="Errors" value={report.error_rows} accent="danger" />
                            <MiniStat label="Parking to link" value={report.will_create_parking} />
                        </div>
                        <div className="border border-brand-line rounded-sm max-h-80 overflow-y-auto">
                            <table className="w-full text-xs">
                                <thead className="bg-brand-sage/60 sticky top-0">
                                    <tr>
                                        <th className="text-left p-2">Row</th>
                                        <th className="text-left p-2">Block-Flat</th>
                                        <th className="text-left p-2">Status</th>
                                        <th className="text-left p-2">Issues</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {report.rows.map((r) => (
                                        <tr key={r.row} data-testid={`master-import-row-${r.row}`} className="border-t border-brand-line">
                                            <td className="p-2">{r.row}</td>
                                            <td className="p-2">{r.block}-{r.number}</td>
                                            <td className="p-2">
                                                <Chip data-testid={`master-import-row-status-${r.row}`} variant={STATUS_VARIANT[r.status]}>{r.status}</Chip>
                                            </td>
                                            <td className="p-2 text-brand-inkSoft">
                                                {r.errors?.length > 0 ? (
                                                    <ul className="space-y-1">
                                                        {r.errors.map((e, i) => (
                                                            <li key={i}><b>{e.field}:</b> {e.problem}</li>
                                                        ))}
                                                    </ul>
                                                ) : r.status === "duplicate" ? "Already exists — will be skipped" : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {phase === "result" && commitResult && (
                    <div className="space-y-4" data-testid="master-import-result">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <MiniStat label="Flats created" value={commitResult.created_flats} accent="success" />
                            <MiniStat label="Parking linked" value={commitResult.created_parking} accent="success" />
                            <MiniStat label="Duplicates skipped" value={commitResult.duplicate_rows} accent="neutral" />
                            <MiniStat label="Errors" value={commitResult.error_rows} accent="danger" />
                        </div>
                        {commitResult.commit_errors?.length > 0 && (
                            <div className="border border-brand-line rounded-sm p-3 text-xs bg-[#FEF2F2]/40">
                                <p className="font-medium text-brand-action mb-1">Commit errors</p>
                                <ul className="space-y-1 text-brand-inkSoft">
                                    {commitResult.commit_errors.map((e, i) => <li key={i}>{e}</li>)}
                                </ul>
                            </div>
                        )}
                    </div>
                )}

                <DialogFooter>
                    {phase === "select" && (
                        <>
                            <Button type="button" variant="outline" onClick={close} className="rounded-full">Cancel</Button>
                            <Button type="button" data-testid="master-import-preview-btn" disabled={!file || busy} onClick={preview}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {busy ? "Validating..." : "Preview"}
                            </Button>
                        </>
                    )}
                    {phase === "preview" && (
                        <>
                            <Button type="button" data-testid="master-import-back-btn" variant="outline" onClick={() => setPhase("select")} className="rounded-full">Back</Button>
                            <Button type="button" data-testid="master-import-commit-btn" disabled={busy || report.valid_rows === 0} onClick={commit}
                                className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {busy ? "Importing..." : `Commit Import (${report.valid_rows} valid)`}
                            </Button>
                        </>
                    )}
                    {phase === "result" && (
                        <Button type="button" data-testid="master-import-done-btn" onClick={close} className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                            Done
                        </Button>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function MiniStat({ label, value, accent }) {
    return (
        <div className="border border-brand-line rounded-sm p-3 bg-white">
            <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{label}</p>
            <p className={`font-heading text-xl mt-1 ${accent === "success" ? "text-[#059669]" : accent === "danger" ? "text-brand-action" : "text-brand-ink"}`}>{value ?? 0}</p>
        </div>
    );
}
